import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon } from 'lucide-react';
export type FieldType = 'text' | 'number' | 'date' | 'select';
export interface FieldConfig {
  id: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  options?: string[];
}
interface FieldCellProps {
  field: FieldConfig;
  value: string;
  savedValue: string;
  onChange: (val: string) => void;
  isLastInRow?: boolean;
  isLastRow?: boolean;
}
export function FieldCell({
  field,
  value,
  savedValue,
  onChange,
  isLastInRow,
  isLastRow
}: FieldCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement>(null);
  const isCompleted = savedValue.trim() !== '';
  const isPending = value.trim() !== '' && value !== savedValue;
  const isEmpty = value.trim() === '';
  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);
  const handleContainerClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    }
  };
  const handleBlur = () => {
    setIsEditing(false);
  };
  // Build border classes cleanly
  const getBorderClasses = () => {
    const classes: string[] = [];
    // Mobile: border-b on all except last row
    if (!isLastRow) {
      classes.push('border-b border-slate-100');
    }
    // Desktop: add right border on left column cells (not last in row)
    if (!isLastInRow) {
      classes.push('md:border-r md:border-slate-100');
    }
    return classes.join(' ');
  };
  const borderClasses = getBorderClasses();
  // Container state classes
  const getStateClasses = () => {
    if (isEditing) {
      return 'bg-indigo-50/40 ring-1 ring-inset ring-indigo-200';
    }
    if (isCompleted && !isPending) {
      return 'bg-slate-50/50 hover:bg-slate-50';
    }
    return 'hover:bg-slate-50/80';
  };
  return <div className={`
        p-5 flex flex-col justify-center min-h-[84px] transition-all duration-200 
        relative group cursor-pointer
        ${borderClasses}
        ${getStateClasses()}
      `} onClick={handleContainerClick} data-id="element-3">
      {/* Subtle left border accent on hover for empty/pending fields */}
      {!isEditing && !isCompleted && <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-indigo-300 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4" />}

      {/* Label row */}
      <div className="flex items-center justify-between mb-1.5" data-id="element-5">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer" data-id="element-6">
          {field.label}
        </label>

        <div className="flex items-center gap-2" data-id="element-7">
          {!isEditing && isEmpty && <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-100" data-id="element-8">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500" data-id="element-9" />
              Required
            </span>}
          {!isEditing && isCompleted && !isPending && <div className="bg-emerald-100 p-1 rounded-full" data-id="element-10">
              <CheckIcon className="w-3.5 h-3.5 text-emerald-600" data-id="element-11" />
            </div>}
          {!isEditing && isPending && <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200" data-id="element-12">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" data-id="element-13" />
              Unsaved
            </span>}
        </div>
      </div>

      {/* Input area */}
      {isEditing ? <div className="mt-0.5 animate-in fade-in duration-150" data-id="element-14">
          {field.type === 'select' ? <select ref={inputRef as React.RefObject<HTMLSelectElement>} value={value} onChange={e => onChange(e.target.value)} onBlur={handleBlur} className="w-full text-sm text-slate-900 border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm transition-all" data-id="element-15">
              <option value="" disabled data-id="element-16">
                Select an option
              </option>
              {field.options?.map(opt => <option key={opt} value={opt} data-id="element-17">
                  {opt}
                </option>)}
            </select> : <input ref={inputRef as React.RefObject<HTMLInputElement>} type={field.type} placeholder={field.placeholder} value={value} onChange={e => onChange(e.target.value)} onBlur={handleBlur} className="w-full text-sm text-slate-900 border border-indigo-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white shadow-sm transition-all placeholder:text-slate-400" data-id="element-18" />}
        </div> : <div className="mt-0.5" data-id="element-19">
          {isEmpty ? <div className="w-full rounded-lg h-9 flex items-center px-3 text-slate-400 text-sm bg-slate-50/80 border border-dashed border-slate-300 group-hover:bg-white group-hover:border-solid group-hover:border-slate-400 transition-all" data-id="element-20">
              Click to add {field.label.toLowerCase()}
            </div> : <div className="text-sm font-medium text-slate-900 truncate px-1 py-1.5" data-id="element-21">
              {value}
            </div>}
        </div>}
    </div>;
}